import { AiSearchProvider } from "@redotech/redo-model/aeo/ai-search-providers";
import { assertNever } from "@redotech/util/type";

export enum MerchantEmailTrigger {
  DISPUTE_MANUAL_EDIT = "dispute_manual_edit",
  DISPUTE_ONBOARDING_COMPLETE = "dispute_onboarding_complete",
  DISPUTE_ONBOARDING_AUTOMATION_DECLINED = "dispute_onboarding_automation_declined",
  DISPUTE_RECEIVED_NOT_ENROLLED = "dispute_received_not_enrolled",
  DISPUTE_TAB_VIEWED_NO_ACTION = "dispute_tab_viewed_no_action",
  RECLAIM_DISPUTE_WON_RECOVER_NOT_ENROLLED = "reclaim_dispute_won_recover_not_enrolled",
  RECOVER_PAGE_VIEWED_NOT_ENROLLED = "recover_page_viewed_not_enrolled",
  RECOVER_AUTOMATION_VIEWED_NOT_ENROLLED = "recover_automation_viewed_not_enrolled",
  AGENTIC_SHOPPING_ORDER_WITHOUT_AEO = "agentic_shopping_order_without_aeo",
  REFUND_PROCESSED_RECOVER_NOT_ENROLLED = "refund_processed_recover_not_enrolled",
  RECOVER_DAILY_ACTIVITY_SUMMARY = "recover_daily_activity_summary",
  CHOP_TEST_COMPLETE_RECOVER_NOT_ENROLLED = "chop_test_complete_recover_not_enrolled",
  RECOVER_CART_RECOVERED = "recover_cart_recovered",
  THEME_PUBLISHED_CHOP_NOT_ENROLLED = "theme_published_chop_not_enrolled",
  THEME_UPDATED_CHOP_NOT_ENROLLED = "theme_updated_chop_not_enrolled",
  ORDER_FULFILLED_COULD_HAVE_AUTO_MERGED = "order_fulfilled_could_have_auto_merged",
}

export const RECOVER_PROJECTED_RECOVERY_RATE = 0.02;
export const CHOP_PROJECTED_REVENUE_RATE = 0.03;

export enum MerchantEmailRecipientType {
  TEAM_OWNER = "team_owner",
  NOTIFICATION_EMAIL = "notification_email",
  ALL_TEAM_ADMINS = "all_team_admins",
}

export enum MerchantEmailFrequency {
  EVERY_TIME = "every_time",
  COOLDOWN = "cooldown",
  ONCE_PER_TEAM = "once_per_team",
}

export enum MerchantEmailCampaignStatus {
  NEW = "new",
  ACTIVE = "active",
  ARCHIVED = "archived",
}

export function getMerchantEmailTriggerMeta(trigger: MerchantEmailTrigger): {
  label: string;
  description: string;
} {
  switch (trigger) {
    case MerchantEmailTrigger.DISPUTE_MANUAL_EDIT:
      return {
        label: "Dispute Manual Edit",
        description:
          "Merchant uploaded or edited dispute evidence in Shopify instead of through Redo.",
      };
    case MerchantEmailTrigger.DISPUTE_ONBOARDING_COMPLETE:
      return {
        label: "Dispute Onboarding Complete",
        description:
          "Merchant completed dispute onboarding with automation enabled. Encourage full integration.",
      };
    case MerchantEmailTrigger.DISPUTE_ONBOARDING_AUTOMATION_DECLINED:
      return {
        label: "Dispute Onboarding Automation Declined",
        description:
          "Merchant enabled disputes but chose not to enable AI automation.",
      };
    case MerchantEmailTrigger.DISPUTE_RECEIVED_NOT_ENROLLED:
      return {
        label: "Dispute Received Not Enrolled",
        description:
          "A new dispute was received for a merchant who has not enrolled in Redo's dispute product.",
      };
    case MerchantEmailTrigger.DISPUTE_TAB_VIEWED_NO_ACTION:
      return {
        label: "Dispute Tab Viewed No Action",
        description:
          "Merchant visited the disputes tab but navigated away without interacting.",
      };
    case MerchantEmailTrigger.RECLAIM_DISPUTE_WON_RECOVER_NOT_ENROLLED:
      return {
        label: "Reclaim Dispute Won — Recover Not Enrolled",
        description:
          "A dispute was won for a team using Reclaim that has not enrolled in the Recover product.",
      };
    case MerchantEmailTrigger.RECOVER_PAGE_VIEWED_NOT_ENROLLED:
      return {
        label: "Recover Page Viewed Not Enrolled",
        description:
          "Merchant visited the Recover welcome page but has not enrolled in the Recover product.",
      };
    case MerchantEmailTrigger.RECOVER_AUTOMATION_VIEWED_NOT_ENROLLED:
      return {
        label: "Recover Automation Viewed Not Enrolled",
        description:
          "Merchant viewed the marketing automations page but has not enrolled in the Recover product.",
      };
    case MerchantEmailTrigger.AGENTIC_SHOPPING_ORDER_WITHOUT_AEO:
      return {
        label: "Agentic Shopping Order Without AEO",
        description:
          "Merchant received an order referred from an agentic shopping channel (ChatGPT, Perplexity, Gemini) and is not enrolled in AEO.",
      };
    case MerchantEmailTrigger.REFUND_PROCESSED_RECOVER_NOT_ENROLLED:
      return {
        label: "Refund Processed — Recover Not Enrolled",
        description:
          "A refund was processed for a return on a team that has not enrolled in the Recover product.",
      };
    case MerchantEmailTrigger.RECOVER_DAILY_ACTIVITY_SUMMARY:
      return {
        label: "Recover Daily Activity Summary",
        description:
          "Daily summary of Recover messaging activity for teams with active Recover automations.",
      };
    case MerchantEmailTrigger.CHOP_TEST_COMPLETE_RECOVER_NOT_ENROLLED:
      return {
        label: "CHOP Test Complete — Recover Not Enrolled",
        description:
          "A checkout optimization A/B test was concluded for a team that has not enrolled in the Recover product.",
      };
    case MerchantEmailTrigger.RECOVER_CART_RECOVERED:
      return {
        label: "Recover Cart Recovered",
        description:
          "An abandoned cart was successfully recovered through the Recover product — an order was attributed to a Recover conversation.",
      };
    case MerchantEmailTrigger.THEME_PUBLISHED_CHOP_NOT_ENROLLED:
      return {
        label: "Theme Published — Chop Not Enrolled",
        description:
          "A theme was published for a merchant that has not enrolled in the Chop product.",
      };
    case MerchantEmailTrigger.THEME_UPDATED_CHOP_NOT_ENROLLED:
      return {
        label: "Theme Updated — Chop Not Enrolled",
        description:
          "A theme was updated for a merchant that has not enrolled in the Chop product.",
      };
    case MerchantEmailTrigger.ORDER_FULFILLED_COULD_HAVE_AUTO_MERGED:
      return {
        label: "Order Fulfilled — Could Have Auto-Merged",
        description:
          "An order was fulfilled on a team that is not enrolled in OMS, and Snowflake shows another recent order from the same customer/address that Redo OMS auto-merge could have combined it with.",
      };
    default:
      assertNever(trigger);
  }
}

/**
 * Maps triggers to their required triggerData shape.
 * Triggers not listed here require no extra data.
 */
export interface MerchantEmailTriggerDataMap {
  [MerchantEmailTrigger.RECLAIM_DISPUTE_WON_RECOVER_NOT_ENROLLED]: {
    disputeAmount: number;
  };
  [MerchantEmailTrigger.AGENTIC_SHOPPING_ORDER_WITHOUT_AEO]: {
    channel: AiSearchProvider;
  };
  [MerchantEmailTrigger.RECOVER_DAILY_ACTIVITY_SUMMARY]: {
    messagesSent: number;
    recoveredRevenue: number;
    recoveredOrders: number;
  };
  [MerchantEmailTrigger.RECOVER_CART_RECOVERED]: {
    recoveredOrderValue: number;
  };
  [MerchantEmailTrigger.ORDER_FULFILLED_COULD_HAVE_AUTO_MERGED]: {
    /** Count of fulfilled orders in the last 30 days that had a mergeable sibling. */
    mergeableOrdersLast30Days: number;
    /** Estimated label-cost savings (USD) from avoided duplicate shipments over the last 30 days. */
    potentialSavingsLast30DaysUsd: number;
    /** Most recent fulfilled order with a mergeable sibling. */
    mostRecentOrderName: string;
    mostRecentOrderUrl: string;
    /** The sibling order the most recent one would have been merged with. */
    mostRecentSiblingOrderName: string;
    mostRecentSiblingOrderUrl: string;
  };
}

type TriggersWithData = keyof MerchantEmailTriggerDataMap;
type TriggersWithoutData = Exclude<MerchantEmailTrigger, TriggersWithData>;

export type MerchantEmailTriggerInput =
  | {
      [K in TriggersWithData]: {
        trigger: K;
        triggerData: MerchantEmailTriggerDataMap[K];
      };
    }[TriggersWithData]
  | { trigger: TriggersWithoutData };

export interface MerchantEmailVariableMeta {
  name: string;
  description: string;
  example: string;
}

/** Always populated from team data. */
export const MERCHANT_EMAIL_BASE_VARIABLES: readonly MerchantEmailVariableMeta[] =
  [
    {
      name: "teamId",
      description: "The team's unique identifier",
      example: "507f1f77bcf86cd799439011",
    },
    {
      name: "teamName",
      description: "The team's display name",
      example: "Acme Store",
    },
  ];

/** Resolved at fire time via Shopify API. */
export const MERCHANT_EMAIL_COMPUTED_VARIABLES: readonly MerchantEmailVariableMeta[] =
  [
    {
      name: "monthlyOrders",
      description: "Number of orders in the last 30 days",
      example: "4,200",
    },
    {
      name: "averageOrderValue",
      description: "Average order value in the last 30 days",
      example: "$85.00",
    },
    {
      name: "monthlyProjectedRecoveredRevenue",
      description:
        "Estimated monthly recovered revenue (orders x AOV x 2% recovery rate)",
      example: "$7,140",
    },
    {
      name: "monthlyProjectedChopRevenue",
      description: "Estimated monthly Chop revenue (orders x AOV x 3%)",
      example: "$10,710",
    },
  ];

/**
 * Trigger-specific variables, keyed by trigger.
 * Each entry's triggerData field (from MerchantEmailTriggerDataMap)
 * produces the template variables listed here.
 */
export const MERCHANT_EMAIL_TRIGGER_VARIABLES: Partial<
  Record<MerchantEmailTrigger, readonly MerchantEmailVariableMeta[]>
> = {
  // triggerData.disputeAmount → {{disputeAmountWon}}
  [MerchantEmailTrigger.RECLAIM_DISPUTE_WON_RECOVER_NOT_ENROLLED]: [
    {
      name: "disputeAmountWon",
      description: "Amount of the most recently won dispute",
      example: "$150",
    },
  ],
  // triggerData.channel → {{agenticChannel}}
  [MerchantEmailTrigger.AGENTIC_SHOPPING_ORDER_WITHOUT_AEO]: [
    {
      name: "agenticChannel",
      description:
        "Friendly name of the agentic shopping channel that referred the order",
      example: "ChatGPT",
    },
  ],
  // triggerData.messagesSent → {{recoverMessagesSentToday}}, etc.
  [MerchantEmailTrigger.RECOVER_DAILY_ACTIVITY_SUMMARY]: [
    {
      name: "recoverMessagesSentToday",
      description: "Number of Recover messages sent today",
      example: "42",
    },
    {
      name: "recoverRevenueToday",
      description: "Revenue recovered through Recover today",
      example: "$3,850",
    },
    {
      name: "recoverOrdersToday",
      description: "Number of orders recovered through Recover today",
      example: "12",
    },
  ],
  // triggerData.recoveredOrderValue → {{recoveredOrderValue}}
  [MerchantEmailTrigger.RECOVER_CART_RECOVERED]: [
    {
      name: "recoveredOrderValue",
      description: "Value of the recovered order",
      example: "$127",
    },
  ],
  [MerchantEmailTrigger.ORDER_FULFILLED_COULD_HAVE_AUTO_MERGED]: [
    {
      name: "mergeableOrdersLast30Days",
      description:
        "Number of fulfilled orders in the last 30 days that Redo OMS auto-merge would have combined with another shipment",
      example: "14",
    },
    {
      name: "potentialSavingsLast30Days",
      description:
        "Estimated label-cost savings over the last 30 days from those avoided duplicate shipments",
      example: "$84",
    },
    {
      name: "mostRecentMergeableOrder",
      description:
        "The merchant-facing name of the most recent fulfilled order that could have been auto-merged (e.g. #1001)",
      example: "#1001",
    },
    {
      name: "mostRecentMergeableOrderUrl",
      description:
        "A link to that most recent order in the merchant's storefront admin",
      example: "https://acme-store.myshopify.com/admin/orders/1234567890",
    },
    {
      name: "mostRecentMergeableSibling",
      description:
        "The merchant-facing name of the sibling order the most recent one would have been merged with",
      example: "#1002",
    },
    {
      name: "mostRecentMergeableSiblingUrl",
      description:
        "A link to the sibling order in the merchant's storefront admin",
      example: "https://acme-store.myshopify.com/admin/orders/1234567891",
    },
  ],
};

export const MERCHANT_EMAIL_VARIABLES: readonly MerchantEmailVariableMeta[] = [
  ...MERCHANT_EMAIL_BASE_VARIABLES,
  ...MERCHANT_EMAIL_COMPUTED_VARIABLES,
  ...Object.values(MERCHANT_EMAIL_TRIGGER_VARIABLES).flat(),
];

export function extractMerchantEmailVariables(template: string): string[] {
  const names = new Set<string>();
  const known = new Set(MERCHANT_EMAIL_VARIABLES.map((v) => v.name));
  for (const match of template.matchAll(/\{\{(\w+)\}\}/g)) {
    const name = match[1];
    if (name !== undefined && known.has(name)) {
      names.add(name);
    }
  }
  return [...names];
}

export function interpolateMerchantEmailTemplate(
  template: string,
  variables: Record<string, string>,
): string {
  return template.replace(
    /\{\{(\w+)\}\}/g,
    (match, key: string) => variables[key] ?? match,
  );
}

export function getMerchantEmailCampaignStatusMeta(
  status: MerchantEmailCampaignStatus,
): { label: string; badgeColor: "gray" | "success" | "warning" } {
  switch (status) {
    case MerchantEmailCampaignStatus.NEW:
      return { label: "New", badgeColor: "gray" };
    case MerchantEmailCampaignStatus.ACTIVE:
      return { label: "Active", badgeColor: "success" };
    case MerchantEmailCampaignStatus.ARCHIVED:
      return { label: "Archived", badgeColor: "warning" };
    default:
      assertNever(status);
  }
}
